export type FirewallFarbe = 'gruen' | 'gelb' | 'rot';

export const FARBE_FILL: Record<FirewallFarbe, string> = {
  gruen: '#92D050',
  gelb: '#FFFF00',
  rot: '#EE0000',
};

export const FARBE_LABEL: Record<FirewallFarbe, string> = {
  gruen: 'Grün',
  gelb: 'Gelb',
  rot: 'Rot',
};

export const FARBE_TEXT: Record<FirewallFarbe, string> = {
  gruen: '#1b3a0e',
  gelb: '#4a4200',
  rot: '#ffffff',
};

/** Bewertungs-Karten im Swipe und Finalen Check (Mockup: Kritisch / Prüfen / Unbedenklich). */
export const RATING_TITLE: Record<FirewallFarbe, string> = {
  rot: 'Kritisch',
  gelb: 'Prüfen',
  gruen: 'Unbedenklich',
};

export const RATING_SUBTITLE: Record<FirewallFarbe, string> = {
  rot: 'Regel stellt ein Risiko dar.',
  gelb: 'Weitere Analyse erforderlich.',
  gruen: 'Kein relevantes Risiko erkennbar.',
};

/** UI-Farben der Bewertung (Mockup); `FARBE_FILL` bleibt für Punkte, Balken und DOCX. */
export const RATING_UI_COLOR: Record<FirewallFarbe, string> = {
  rot: '#ef4444',
  gelb: '#eab308',
  gruen: '#22c55e',
};

export const RATING_ORDER: FirewallFarbe[] = ['rot', 'gelb', 'gruen'];

export const COMMENT_MAX_LENGTH = 500;

export type RatingState = FirewallFarbe | 'offen';

export function ratingStateLabel(state: RatingState): string {
  return state === 'offen' ? 'Noch nicht bewertet' : RATING_TITLE[state];
}

/** Eigene Farbe im Swipe, Konsens im Finalen Check; `offen` ohne Bewertung. */
export function ratingState(
  rule: { eigene_bewertung?: { farbe: FirewallFarbe } | null; konsens?: { farbe: FirewallFarbe } | null },
  mode: 'swipe' | 'final',
): RatingState {
  const farbe = mode === 'final' ? rule.konsens?.farbe : rule.eigene_bewertung?.farbe;
  return farbe ?? 'offen';
}

export type VorlagenSprache = 'de' | 'en';

interface VorlageLike {
  button_label_de: string;
  button_label_en: string;
  text_de: string;
  text_en: string;
}

export function vorlagenSprache(sprache: string | null | undefined): VorlagenSprache {
  return (sprache ?? 'de').toLowerCase().startsWith('en') ? 'en' : 'de';
}

function pickLanguage(primary: string, secondary: string): string {
  return primary.trim() ? primary : secondary;
}

/** Text der Vorlage in der Audit-Sprache, sonst in der jeweils anderen Sprache. */
export function vorlageText(vorlage: VorlageLike, sprache: string | null | undefined): string {
  return vorlagenSprache(sprache) === 'en'
    ? pickLanguage(vorlage.text_en, vorlage.text_de)
    : pickLanguage(vorlage.text_de, vorlage.text_en);
}

export function vorlageLabel(vorlage: VorlageLike, sprache: string | null | undefined): string {
  return vorlagenSprache(sprache) === 'en'
    ? pickLanguage(vorlage.button_label_en, vorlage.button_label_de)
    : pickLanguage(vorlage.button_label_de, vorlage.button_label_en);
}

export function appendSnippet(current: string, snippet: string): string {
  if (current.length === 0) return snippet;
  return `${current}\n${snippet}`;
}

/** Kommentarpflicht: Kritisch (rot) und Prüfen (gelb) brauchen Text; Whitespace zählt als leer. */
export function ratingBlocked(farbe: FirewallFarbe, text: string): boolean {
  return (farbe === 'gelb' || farbe === 'rot') && text.trim().length === 0;
}

export function commentRequired(farbe: FirewallFarbe | null): boolean {
  return farbe === 'gelb' || farbe === 'rot';
}

export const COMMENT_REQUIRED_HINT = 'Für Kritisch und Prüfen ist ein Kommentar erforderlich.';

/** Grund, warum „Bewerten & weiter“ noch gesperrt ist; `null` = speicherbar. */
export function submitHint(farbe: FirewallFarbe | null, text: string): string | null {
  if (!farbe) return 'Bewertung auswählen, um fortzufahren.';
  if (ratingBlocked(farbe, text)) return COMMENT_REQUIRED_HINT;
  return null;
}

export function firstOpenRuleIndex(regeln: { eigene_bewertung?: unknown | null }[]): number {
  const index = regeln.findIndex((regel) => !regel.eigene_bewertung);
  return index >= 0 ? index : 0;
}

export function firstWithoutConsensus(regeln: { konsens?: unknown | null }[]): number {
  const index = regeln.findIndex((regel) => !regel.konsens);
  return index >= 0 ? index : 0;
}

export function colorsDisagree(farben: Array<string | null | undefined>): boolean {
  const present = farben.filter((farbe): farbe is string => Boolean(farbe));
  return new Set(present).size > 1;
}

export function joinList(values: unknown[] | null | undefined): string {
  const parts = (values ?? []).map((value) => String(value)).filter((value) => value.trim().length > 0);
  return parts.length > 0 ? parts.join(', ') : '–';
}

export function initials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((part) => part[0]).join('');
  return letters.toUpperCase() || '?';
}

/** 12 kB, 1.5 GB … aus einer Byte-Zahl; `–` bei unbekannt. */
export function formatBytes(value: number | null | undefined): string {
  if (value == null) return '–';
  if (value === 0) return '0 B';
  const units = ['B', 'kB', 'MB', 'GB', 'TB', 'PB'];
  let index = 0;
  let current = value;
  while (current >= 1024 && index < units.length - 1) {
    current /= 1024;
    index += 1;
  }
  const digits = current >= 100 || index === 0 ? 0 : current >= 10 ? 1 : 2;
  return `${current.toFixed(digits)} ${units[index]}`;
}

export const BYTES_BUCKET_LABELS = ['0', 'B', '', '', 'kB', '', '', 'MB', '', '', 'GB', '', '', 'TB'];

export interface AddressIpWiderspruch {
  aufgeloest: string;
  im_namen: string;
}

export interface AddressLike {
  name: string;
  aufgeloest: boolean;
  wert?: string | null;
  grund?: string | null;
  ip_widerspruch?: AddressIpWiderspruch | null;
}

export interface AddressView {
  headline: string;
  secondary: string | null;
  keineIp: boolean;
  hinweis: string | null;
  widerspruch: AddressIpWiderspruch | null;
  monospace: boolean;
}

/** Aufgelöste Adresse als Überschrift. Ohne Auflösung nur der Name, nie eine aus ihm gelesene IP. */
export function addressView(item: AddressLike): AddressView {
  if (item.aufgeloest && item.wert) {
    return {
      headline: item.wert,
      secondary: item.wert !== item.name ? item.name : null,
      keineIp: false,
      hinweis: null,
      widerspruch: item.ip_widerspruch ?? null,
      monospace: true,
    };
  }
  return {
    headline: item.name,
    secondary: null,
    keineIp: true,
    hinweis: item.aufgeloest
      ? 'Adressobjekt ohne IP'
      : item.grund === 'keine_adressdatei'
        ? 'Addresses-Datei fehlt'
        : 'nicht aufgelöst',
    widerspruch: null,
    monospace: false,
  };
}

// ── Ähnliche Regeln, Abweichung, Filter ───────────────────────────────────

interface RuleLike {
  id: number;
  vdom?: string | null;
  source: string[];
  destination: string[];
  service: string[];
  action: string;
  abschnitt: string;
  policy_id: string;
  anzeigename: string;
  bewertungen: { mitarbeiter_id: number; farbe: FirewallFarbe }[];
  eigene_bewertung?: { farbe: FirewallFarbe } | null;
  konsens?: { farbe: FirewallFarbe } | null;
  services?: { risiko: boolean; port_von?: number | null; port_bis?: number | null; name?: string | null; raw: string }[];
  risiken?: { code: string; stufe: 'rot' | 'gelb' }[];
  ist_any?: boolean;
  source_aufgeloest?: { wert?: string | null; werte?: string[] }[];
  destination_aufgeloest?: { wert?: string | null; werte?: string[] }[];
  interface_pair?: string;
}

export type SimilarityReason = 'ziel' | 'quelle' | 'service';

export interface SimilarRule<T extends RuleLike> {
  rule: T;
  gruende: SimilarityReason[];
}

function sameSet(a: string[], b: string[]): boolean {
  if (a.length === 0 && b.length === 0) return false;
  const left = new Set(a.map((item) => item.trim().toLowerCase()));
  const right = new Set(b.map((item) => item.trim().toLowerCase()));
  if (left.size !== right.size) return false;
  return Array.from(left).every((item) => right.has(item));
}

function isAllOnly(values: string[]): boolean {
  return values.length === 1 && values[0].trim().toLowerCase() === 'all';
}

/**
 * Regeln desselben VDOM mit gleichem Ziel, gleicher Quelle oder gleichem Service-Set.
 * Treffer nur über `all` (Quelle oder Ziel) oder nur über `ALL` als Service zählen nicht.
 */
export function findSimilarRules<T extends RuleLike>(rule: T, rules: T[], limit = 12): SimilarRule<T>[] {
  const result: SimilarRule<T>[] = [];
  for (const other of rules) {
    if (other.id === rule.id) continue;
    if ((other.vdom ?? '') !== (rule.vdom ?? '')) continue;
    const gruende: SimilarityReason[] = [];
    if (!isAllOnly(rule.destination) && sameSet(rule.destination, other.destination)) gruende.push('ziel');
    if (!isAllOnly(rule.source) && sameSet(rule.source, other.source)) gruende.push('quelle');
    if (!isAllOnly(rule.service) && sameSet(rule.service, other.service)) gruende.push('service');
    if (gruende.length > 0) result.push({ rule: other, gruende });
  }
  result.sort((a, b) => b.gruende.length - a.gruende.length);
  return result.slice(0, limit);
}

export function ruleDisagrees(rule: Pick<RuleLike, 'bewertungen' | 'konsens'>): boolean {
  if (rule.konsens) return false;
  return colorsDisagree(rule.bewertungen.map((item) => item.farbe));
}

export function ratingByMember(rule: Pick<RuleLike, 'bewertungen'>, memberId: number): FirewallFarbe | null {
  return rule.bewertungen.find((item) => item.mitarbeiter_id === memberId)?.farbe ?? null;
}

export interface RuleFilters {
  abschnitt: string | 'alle';
  farbe: FirewallFarbe | 'offen' | 'alle';
  offenFuer: number | 'alle';
  risiko: boolean;
  any: boolean;
  abweichung: boolean;
  suche: string;
}

export const DEFAULT_RULE_FILTERS: RuleFilters = {
  abschnitt: 'alle',
  farbe: 'alle',
  offenFuer: 'alle',
  risiko: false,
  any: false,
  abweichung: false,
  suche: '',
};

export function isAnyRule(rule: Pick<RuleLike, 'ist_any' | 'risiken'>): boolean {
  return rule.ist_any ?? (rule.risiken ?? []).some((item) => item.code === 'any');
}

export function riskCount(rule: Pick<RuleLike, 'risiken'>): number {
  return (rule.risiken ?? []).length;
}

function ruleColor(rule: Pick<RuleLike, 'konsens' | 'bewertungen' | 'eigene_bewertung'>): FirewallFarbe | null {
  if (rule.konsens) return rule.konsens.farbe;
  const farben = rule.bewertungen.map((item) => item.farbe);
  // In der Bewertung liefert der Server fremde Farben nicht mit; die eigene zählt trotzdem.
  const eigene = rule.eigene_bewertung?.farbe;
  if (eigene && !farben.includes(eigene)) farben.push(eigene);
  if (farben.length === 0) return null;
  const order: FirewallFarbe[] = ['rot', 'gelb', 'gruen'];
  return order.find((farbe) => farben.includes(farbe)) ?? null;
}

/** Dieselbe Farbe wie die Übersicht: Konsens, sonst die stärkste Einzelbewertung. */
export function ruleDisplayColor(rule: Pick<RuleLike, 'konsens' | 'bewertungen'>): FirewallFarbe | null {
  return ruleColor(rule);
}

export function matchesFarbe(
  rule: Pick<RuleLike, 'konsens' | 'bewertungen'>,
  farbe: RuleFilters['farbe'],
): boolean {
  if (farbe === 'alle') return true;
  const color = ruleColor(rule);
  if (farbe === 'offen') return color === null;
  return color === farbe;
}

export function parseFarbeParam(value: string | null | undefined): RuleFilters['farbe'] {
  if (value === 'rot' || value === 'gelb' || value === 'gruen' || value === 'offen') return value;
  return 'alle';
}

/** Nächste Regel der aktuellen Reihe. `null`, wenn es keine weitere gibt. */
export function nextRuleId<T extends { id: number }>(sequence: readonly T[], currentId: number): number | null {
  const at = sequence.findIndex((item) => item.id === currentId);
  if (at < 0 || at + 1 >= sequence.length) return null;
  return sequence[at + 1].id;
}

/**
 * Nächste noch offene Regel, wie „Bewerten & weiter“ früher sprang:
 * vorwärts, sonst die erste offene davor. Im Finalen Check zählt fehlender Konsens.
 */
export function nextUnratedId<
  T extends { id: number; eigene_bewertung?: unknown | null; konsens?: unknown | null },
>(sequence: readonly T[], currentId: number, mode: 'swipe' | 'final'): number | null {
  const at = sequence.findIndex((item) => item.id === currentId);
  const start = at < 0 ? 0 : at + 1;
  const isOpen = (item: T) => (mode === 'swipe' ? !item.eigene_bewertung : !item.konsens);
  const after = sequence.slice(start).find(isOpen);
  if (after) return after.id;
  const before = sequence.slice(0, Math.max(at, 0)).find(isOpen);
  return before ? before.id : null;
}

/** Suche nach Objektname, aufgelöster IP oder Port (Zahl trifft Portbereiche und IANA-Namen). */
export function ruleMatchesSearch(rule: RuleLike, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const haystack: string[] = [
    rule.policy_id,
    rule.anzeigename,
    ...rule.source,
    ...rule.destination,
    ...rule.service,
    rule.interface_pair ?? '',
  ];
  (rule.source_aufgeloest ?? []).concat(rule.destination_aufgeloest ?? []).forEach((item) => {
    if (item.wert) haystack.push(item.wert);
    (item.werte ?? []).forEach((wert) => haystack.push(wert));
  });
  (rule.services ?? []).forEach((service) => {
    if (service.name) haystack.push(service.name);
  });
  if (haystack.some((value) => value.toLowerCase().includes(needle))) return true;
  if (/^\d{1,5}$/.test(needle)) {
    const port = Number(needle);
    return (rule.services ?? []).some(
      (service) => service.port_von != null && service.port_bis != null && service.port_von <= port && port <= service.port_bis,
    );
  }
  return false;
}

export function filterRules<T extends RuleLike>(rules: T[], filters: RuleFilters): T[] {
  return rules.filter((rule) => {
    if (filters.abschnitt !== 'alle' && rule.abschnitt !== filters.abschnitt) return false;
    if (filters.farbe !== 'alle') {
      const color = ruleColor(rule);
      if (filters.farbe === 'offen' ? color !== null : color !== filters.farbe) return false;
    }
    if (filters.offenFuer !== 'alle' && ratingByMember(rule, filters.offenFuer) !== null) return false;
    if (filters.risiko && !(rule.services ?? []).some((service) => service.risiko)) return false;
    if (filters.any && !isAnyRule(rule)) return false;
    if (filters.abweichung && !ruleDisagrees(rule)) return false;
    return ruleMatchesSearch(rule, filters.suche);
  });
}

export interface ColorBarCounts {
  gruen: number;
  gelb: number;
  rot: number;
  offen: number;
}

/** Zählt Regeln nach Konsens- oder stärkster Einzelfarbe; ohne Bewertung = offen. */
export function colorBarCounts(rules: Pick<RuleLike, 'konsens' | 'bewertungen'>[]): ColorBarCounts {
  const counts: ColorBarCounts = { gruen: 0, gelb: 0, rot: 0, offen: 0 };
  rules.forEach((rule) => {
    const color = ruleColor(rule);
    if (color) counts[color] += 1;
    else counts.offen += 1;
  });
  return counts;
}

export function percentileText(perzentil: number | null | undefined, scope: string): string | null {
  if (perzentil == null) return null;
  return `Top ${perzentil} % ${scope}`;
}

/** Klassen der Datenvolumen-Verteilung: 0 bleibt allein, danach je eine Dekade. */
const BYTE_CLASS_LABELS = ['0', '1 B', '10 B', '100 B', '1 kB', '10 kB', '100 kB', '1 MB', '10 MB', '100 MB', '1 GB', '10 GB', '100 GB', '1 TB'] as const;

const BYTE_CLASS_RANGES = [
  '0 B',
  '1–9 B',
  '10–99 B',
  '100–999 B',
  '1–9 kB',
  '10–99 kB',
  '100–999 kB',
  '1–9 MB',
  '10–99 MB',
  '100–999 MB',
  '1–9 GB',
  '10–99 GB',
  '100–999 GB',
  '≥ 1 TB',
] as const;

export interface ByteHistogramClass {
  bucket: number;
  label: string;
  range: string;
  count: number;
  current: boolean;
}

/** Dieselbe Einteilung wie das Backend: 0, sonst 1 + floor(log10), oben bei 1 TB gekappt. */
export function byteClassIndex(bytes: number | null | undefined): number | null {
  if (bytes == null) return null;
  if (bytes <= 0) return 0;
  return Math.min(BYTE_CLASS_LABELS.length - 1, Math.floor(Math.log10(bytes)) + 1);
}

/**
 * Histogramm über die Regeln eines VDOMs.
 * Leere Klassen fallen weg, damit die Säulen die schiefe Verteilung zeigen und nicht an einer linearen Achse kleben.
 */
export function byteHistogram(
  values: ReadonlyArray<number | null | undefined>,
  current: number | null | undefined,
): ByteHistogramClass[] {
  const counts = BYTE_CLASS_LABELS.map(() => 0);
  values.forEach((value) => {
    const bucket = byteClassIndex(value);
    if (bucket != null) counts[bucket] += 1;
  });
  const currentBucket = byteClassIndex(current);
  const classes: ByteHistogramClass[] = [];
  counts.forEach((count, bucket) => {
    if (count === 0 && bucket !== currentBucket) return;
    classes.push({
      bucket,
      label: BYTE_CLASS_LABELS[bucket],
      range: BYTE_CLASS_RANGES[bucket],
      count,
      current: bucket === currentBucket,
    });
  });
  return classes;
}

// ── Dienste & Ports: Gruppen je Protokoll und Portbereich ──────────────

export interface ServiceLike {
  raw: string;
  label: string;
  name?: string | null;
  quelle?: string | null;
  protokoll?: string | null;
  port_von?: number | null;
  port_bis?: number | null;
  bereiche: string[];
  auffaellig: boolean;
  risiko: boolean;
  risiko_grund?: string | null;
  weitere_accept: number;
}

export interface ServiceGroup<T extends ServiceLike> {
  key: string;
  /** Chip in der Protokoll-Spalte: TCP, UDP, ICMP, IP, … */
  protokoll: string;
  /** „4500“, „8081 – 8082“ oder der Rohname bei Diensten ohne Port. */
  port: string;
  /** Klarname (IANA / Fortinet) oder leer. */
  dienst: string;
  /** Weitere Namen aus dem Export für denselben Port, ohne den angezeigten. */
  aliase: string[];
  wildcard: boolean;
  risiko: boolean;
  risikoGrund: string | null;
  weitereAccept: number;
  bereiche: string[];
  quelle: string | null;
  services: T[];
}

function protocolLabel(service: ServiceLike): string {
  const raw = service.raw.trim().toLowerCase();
  if (raw === 'all' || raw === 'ip') return 'ANY';
  if (service.protokoll) return service.protokoll.toUpperCase();
  return '–';
}

function portLabel(service: ServiceLike): string {
  if (service.port_von == null) return service.raw;
  if (service.port_bis != null && service.port_bis !== service.port_von) return `${service.port_von} – ${service.port_bis}`;
  return String(service.port_von);
}

/**
 * Fasst Dienste mit gleichem Protokoll und Portbereich zu einer Zeile zusammen.
 * Wildcard-Dienste (ALL, ALL_ICMP …) bleiben eigene Zeilen und stehen vorn in der Reihenfolge des Exports.
 */
export function groupServices<T extends ServiceLike>(services: T[]): ServiceGroup<T>[] {
  const groups = new Map<string, ServiceGroup<T>>();
  services.forEach((service) => {
    const protokoll = protocolLabel(service);
    const key = service.port_von != null
      ? `${protokoll}|${service.port_von}-${service.port_bis ?? service.port_von}`
      : `${protokoll}|${service.raw.trim().toLowerCase()}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        protokoll,
        port: portLabel(service),
        dienst: (service.name ?? '').trim(),
        aliase: [],
        wildcard: false,
        risiko: false,
        risikoGrund: null,
        weitereAccept: 0,
        bereiche: [],
        quelle: service.quelle ?? null,
        services: [],
      };
      groups.set(key, group);
    }
    const current = group;
    current.services.push(service);
    if (!current.dienst && service.name) current.dienst = service.name.trim();
    current.wildcard = current.wildcard || service.auffaellig;
    if (service.risiko && !current.risiko) {
      current.risiko = true;
      current.risikoGrund = service.risiko_grund ?? null;
    }
    current.weitereAccept = Math.max(current.weitereAccept, service.weitere_accept);
    service.bereiche.forEach((bereich) => {
      if (!current.bereiche.includes(bereich)) current.bereiche.push(bereich);
    });
  });
  const result = Array.from(groups.values());
  result.forEach((group) => {
    const shown = new Set([group.port.toLowerCase(), group.dienst.toLowerCase()]);
    group.aliase = [];
    group.services.forEach((service) => {
      const raw = service.raw.trim();
      if (raw && !shown.has(raw.toLowerCase()) && !group.aliase.includes(raw)) group.aliase.push(raw);
    });
  });
  return result;
}

/**
 * Eine Zeile für die Diensttabelle: Herkunft des Klarnamens, Aliase, weitere Regeln.
 * Ohne diese Angaben bleibt die Zeile leer, damit die Tabellenzeile einzeilig bleibt.
 */
export function serviceGroupDetailLine(
  group: Pick<ServiceGroup<ServiceLike>, 'aliase' | 'bereiche' | 'quelle' | 'risiko' | 'risikoGrund' | 'weitereAccept'>,
): string | null {
  const parts: string[] = [];
  if (group.quelle === 'iana') parts.push('Name aus IANA-Registry');
  else if (group.quelle === 'fortinet') parts.push('Fortinet-Standarddienst');
  if (group.aliase.length > 0) parts.push(`Aliase: ${group.aliase.join(', ')}`);
  if (group.bereiche.length > 1) parts.push(`Bereiche: ${group.bereiche.join(', ')}`);
  if (group.risiko) parts.push(`Risikoport: ${group.risikoGrund ?? 'Stammdaten'}`);
  if (group.weitereAccept > 0) {
    parts.push(`${group.weitereAccept} weitere ACCEPT-Regel${group.weitereAccept === 1 ? '' : 'n'} im Audit mit diesem Dienst`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

// ── Regelliste (Drawer) ───────────────────────────────────────────────

export type DrawerRatingFilter = 'alle' | RatingState;
export type DrawerRiskFilter = 'alle' | 'any' | 'risiko';

export interface DrawerFilters {
  suche: string;
  abschnitt: string | 'alle';
  bewertung: DrawerRatingFilter;
  risiko: DrawerRiskFilter;
}

export const DEFAULT_DRAWER_FILTERS: DrawerFilters = {
  suche: '',
  abschnitt: 'alle',
  bewertung: 'alle',
  risiko: 'alle',
};

export interface DrawerRuleLike extends RuleLike {
  risiken?: { code: string; stufe: 'rot' | 'gelb' }[];
}

/**
 * 1-basierte Position in der Swipe-Reihenfolge eines VDOMs.
 * Die Map kommt aus der ungefilterten Liste, damit Suche und Filter die Nummer nicht verschieben.
 */
export function swipePositions<T extends { id: number }>(rules: T[]): Map<number, number> {
  return new Map(rules.map((rule, index) => [rule.id, index + 1]));
}

/** Filter für die Regelliste: Suche nach Name/ID/Objekt/Port, Abschnitt, Bewertungsstand und Risiko. */
export function filterDrawerRules<T extends DrawerRuleLike>(
  rules: T[],
  filters: DrawerFilters,
  mode: 'swipe' | 'final',
): T[] {
  return rules.filter((rule) => {
    if (filters.abschnitt !== 'alle' && rule.abschnitt !== filters.abschnitt) return false;
    if (filters.bewertung !== 'alle' && ratingState(rule, mode) !== filters.bewertung) return false;
    if (filters.risiko === 'any' && !isAnyRule(rule)) return false;
    if (filters.risiko === 'risiko' && riskCount(rule) === 0) return false;
    return ruleMatchesSearch(rule, filters.suche);
  });
}

// ── Mehrere Firewalls: flache Liste, Deep-Links, Upload-Zuordnung ───────

interface GeraetLike<V> {
  id: number;
  name: string;
  sort_order: number;
  vdoms: V[];
}

interface VdomBlockLike {
  vdom?: string | null;
  label: string;
}

export interface FlatVdom<G, V> {
  geraet: G;
  block: V;
  /** Firewall-Index in der sortierten Liste, VDOM-Index innerhalb der Firewall. */
  geraetIndex: number;
  vdomIndex: number;
}

/** Firewalls nach `sort_order`, dann id; innerhalb der Firewall VDOMs alphabetisch (ohne VDOM zuletzt). */
export function sortGeraete<G extends GeraetLike<VdomBlockLike>>(geraete: G[]): G[] {
  return [...geraete]
    .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id)
    .map((geraet) => ({
      ...geraet,
      vdoms: [...geraet.vdoms].sort((a, b) => {
        if (!a.vdom && b.vdom) return 1;
        if (a.vdom && !b.vdom) return -1;
        return (a.vdom ?? '').localeCompare(b.vdom ?? '', 'de', { sensitivity: 'base' });
      }),
    }));
}

/** Eine flache Liste „Firewall › VDOM“ in Anzeige-Reihenfolge; die Position dient als Tab-Index. */
export function flattenGeraete<G extends GeraetLike<VdomBlockLike>>(geraete: G[]): FlatVdom<G, G['vdoms'][number]>[] {
  const result: FlatVdom<G, G['vdoms'][number]>[] = [];
  sortGeraete(geraete).forEach((geraet, geraetIndex) => {
    geraet.vdoms.forEach((block, vdomIndex) => result.push({ geraet, block, geraetIndex, vdomIndex }));
  });
  return result;
}

/** „Firewall A › root“; bei nur einer Firewall reicht der VDOM-Name. */
export function vdomPathLabel(geraetName: string, vdomLabel: string, geraeteCount: number): string {
  return geraeteCount > 1 ? `${geraetName} › ${vdomLabel}` : vdomLabel;
}

/** Wert für `?vdom=`: der VDOM-Name oder `-` für den Block ohne VDOM. */
export function vdomParam(vdom: string | null | undefined): string {
  return vdom && vdom.trim().length > 0 ? vdom : '-';
}

/** Index in der flachen Liste zu `?fw=&vdom=`; -1, wenn nichts passt. */
export function findFlatIndex<G extends GeraetLike<VdomBlockLike>, V extends VdomBlockLike>(
  flat: FlatVdom<G, V>[],
  fw: string | null | undefined,
  vdom: string | null | undefined,
): number {
  const firewallId = fw ? Number(fw) : NaN;
  if (!Number.isFinite(firewallId)) return -1;
  const wanted = vdom == null || vdom === '' ? null : vdom;
  return flat.findIndex((entry) => entry.geraet.id === firewallId
    && (wanted == null || vdomParam(entry.block.vdom) === wanted));
}

export function reviewLink(
  auditId: number,
  mode: 'swipe' | 'final',
  firewallId: number,
  vdom: string | null | undefined,
  ruleId?: number,
  farbe?: RuleFilters['farbe'] | null,
): string {
  const params = new URLSearchParams({ fw: String(firewallId), vdom: vdomParam(vdom) });
  if (ruleId != null) params.set('regel', String(ruleId));
  if (farbe && farbe !== 'alle') params.set('farbe', farbe);
  return `/audits/${auditId}/firewall/${mode === 'swipe' ? 'bewertung' : 'final'}?${params.toString()}`;
}

/** Nächster freier Name „Firewall N“, Groß-/Kleinschreibung ohne Bedeutung. */
export function nextFirewallName(existing: ReadonlyArray<string>): string {
  const taken = new Set(existing.map((name) => name.trim().toLowerCase()));
  let n = existing.length + 1;
  while (taken.has(`firewall ${n}`)) n += 1;
  for (let candidate = 1; candidate < n; candidate += 1) {
    if (!taken.has(`firewall ${candidate}`)) return `Firewall ${candidate}`;
  }
  return `Firewall ${n}`;
}

/** Auswahl im Upload-Dialog: bestehende Firewall (`id`) oder neue (`neu` + Name). */
export type FirewallChoice =
  | { kind: 'bestehend'; id: number }
  | { kind: 'neu'; name: string }
  | { kind: 'offen' };

export const NEW_FIREWALL_OPTION = '__neu__';

/** Vorbelegung des Firewall-Felds aus dem Backend-Vorschlag. */
export function choiceFromVorschlag(
  vorschlag: { firewall_id?: number | null; neu: boolean; name?: string | null; grund?: string },
  fallbackName: string,
): FirewallChoice {
  if (vorschlag.firewall_id != null) return { kind: 'bestehend', id: vorschlag.firewall_id };
  if (vorschlag.neu) return { kind: 'neu', name: (vorschlag.name ?? '').trim() || fallbackName };
  return { kind: 'offen' };
}

/**
 * Mehrere Dateien in einem Rutsch: dieselbe neue Firewall (gleicher Name, ohne Groß-/Kleinschreibung)
 * wird nur einmal angelegt. Rückgabe: Name → Reihenfolge des ersten Auftretens.
 */
export function newFirewallNames(choices: ReadonlyArray<FirewallChoice>): string[] {
  const seen = new Map<string, string>();
  choices.forEach((choice) => {
    if (choice.kind !== 'neu') return;
    const name = choice.name.trim();
    if (!name) return;
    const key = name.toLowerCase();
    if (!seen.has(key)) seen.set(key, name);
  });
  return Array.from(seen.values());
}

/** Name → id nach dem Anlegen; löst `neu`-Auswahlen in `bestehend` auf. */
export function resolveChoice(choice: FirewallChoice, created: ReadonlyMap<string, number>): FirewallChoice {
  if (choice.kind !== 'neu') return choice;
  const id = created.get(choice.name.trim().toLowerCase());
  return id != null ? { kind: 'bestehend', id } : choice;
}

export interface DrawerGroup<V> {
  key: string;
  label: string;
  flatIndex: number;
  block: V;
}

/**
 * Gruppen für „Alle VDOMs gruppiert“: eine je Firewall › VDOM in Anzeige-Reihenfolge.
 * Bei nur einer Firewall steht nur der VDOM-Name im Gruppenkopf.
 */
export function drawerGroups<G extends GeraetLike<VdomBlockLike>, V extends VdomBlockLike>(flat: FlatVdom<G, V>[]): DrawerGroup<V>[] {
  const geraeteCount = new Set(flat.map((entry) => entry.geraet.id)).size;
  return flat.map((entry, flatIndex) => ({
    key: `${entry.geraet.id}:${vdomParam(entry.block.vdom)}`,
    label: vdomPathLabel(entry.geraet.name, entry.block.label, geraeteCount),
    flatIndex,
    block: entry.block,
  }));
}

/**
 * Sichtbarer Ausschnitt [start, end) einer virtualisierten Liste.
 * `offsets[i]` ist die obere Kante von Zeile i, `offsets[n]` die Gesamthöhe; Zeilenhöhen dürfen variieren.
 */
export function windowRange(offsets: number[], scrollTop: number, viewport: number, overscan = 4): { start: number; end: number } {
  const total = offsets.length - 1;
  if (total <= 0) return { start: 0, end: 0 };
  let lo = 0;
  let hi = total - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (offsets[mid] <= scrollTop) lo = mid;
    else hi = mid - 1;
  }
  let end = lo;
  while (end < total && offsets[end] < scrollTop + viewport) end += 1;
  return { start: Math.max(0, lo - overscan), end: Math.min(total, end + overscan) };
}
